import { ref, computed } from 'vue'
import { useApi } from './useApi'
import type { ApiResponse } from '@/types'

export interface TierBenefits {
  refund_policy: 'standard' | 'always_full'
  cancellation_hours: number
  // Gates the flexible-booking perk: self-chosen whole-hour start/end at the
  // tier-specific hourly rate below (0 = perk disabled for this tier)
  custom_time_slots: boolean
  flexible_hourly_rate_cents: number
}

export interface TierConfig {
  key: string
  slug: string
  name: string
  icon: string
  threshold_cents: number
  discount_percentage: number
  benefits: TierBenefits
}

export interface CreditPackage {
  key: string
  slug: string
  name: string
  description: string | null
  purchase_amount_cents: number
  credit_amount_cents: number
  bonus_percentage: number
  is_popular: boolean
}

export interface TierProgressInfo {
  current_tier: TierConfig
  next_tier: TierConfig | null
  progress_percentage: number
  amount_to_next: number
  current_spending: number
}

export interface UserTierInfo {
  current_tier: TierConfig
  progress: TierProgressInfo
  history: Array<{
    id: number
    old_tier: string | null
    new_tier: string
    total_spent_at_change: number
    reason: string | null
    created_at: string
  }>
  total_spent_lifetime: number
  total_spent_formatted: string
}

export interface TierPreview {
  current_tier: string
  tier_after_purchase: string
  would_upgrade: boolean
  simulated_spending?: number
  package?: CreditPackage
  error?: string
}

export interface PurchaseResponse {
  purchase: {
    purchase_id: number
    purchase_amount: number
    credit_amount: number
    bonus_amount: number
  }
  payment: {
    redirect_url: string
    checkout_session_id?: string
    payment_intent_id?: string | null
  }
  tier_preview: TierPreview
}

export function useLoyalty() {
  // useApi() exposes the axios instance as `instance`, not `api`. Destructuring `api`
  // yields undefined and every loyalty/credit call throws, silently breaking the
  // credit-purchase and tier features. Callers here use axios-response semantics.
  const { instance: api } = useApi()

  const tiers = ref<Record<string, TierConfig>>({})
  const creditPackages = ref<Record<string, CreditPackage>>({})
  const myTierInfo = ref<UserTierInfo | null>(null)

  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Computed properties
  const currentTier = computed(() => myTierInfo.value?.current_tier)
  const nextTier = computed(() => myTierInfo.value?.progress.next_tier ?? null)
  const progressPercentage = computed(() => myTierInfo.value?.progress.progress_percentage || 0)

  /**
   * Fetch all available tiers (public endpoint)
   */
  async function fetchTiers() {
    try {
      isLoading.value = true
      error.value = null

      const response = await api.get<ApiResponse<{ tiers: Record<string, TierConfig> }>>('/loyalty/tiers')

      if (response.data.success) {
        tiers.value = response.data.data?.tiers || {}
      } else {
        throw new Error('Failed to fetch tiers')
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to fetch tiers'
      console.error('Error fetching tiers:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Fetch all available credit packages (public endpoint)
   */
  async function fetchCreditPackages() {
    try {
      isLoading.value = true
      error.value = null

      const response = await api.get<ApiResponse<{ packages: Record<string, CreditPackage> }>>('/loyalty/credit-packages')

      if (response.data.success) {
        creditPackages.value = response.data.data?.packages || {}
      } else {
        throw new Error('Failed to fetch credit packages')
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to fetch credit packages'
      console.error('Error fetching credit packages:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Fetch current user's tier information (requires authentication)
   */
  async function fetchMyTier() {
    try {
      isLoading.value = true
      error.value = null

      const response = await api.get<ApiResponse<UserTierInfo>>('/loyalty/my-tier')

      if (response.data.success && response.data.data) {
        myTierInfo.value = response.data.data
      } else {
        throw new Error('Failed to fetch tier information')
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to fetch your tier information'
      console.error('Error fetching my tier:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Preview tier after purchasing a credit package (requires authentication)
   */
  async function previewPackagePurchase(packageKey: string): Promise<TierPreview | null> {
    try {
      isLoading.value = true
      error.value = null

      const response = await api.get<ApiResponse<{ preview: TierPreview }>>(`/loyalty/package-preview/${packageKey}`)

      if (response.data.success && response.data.data) {
        return response.data.data.preview
      } else {
        throw new Error('Failed to preview package purchase')
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to preview package purchase'
      console.error('Error previewing package:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Purchase a credit package (requires authentication)
   * Redirects to Stripe checkout on success
   */
  async function purchaseCreditPackage(packageKey: string): Promise<PurchaseResponse | null> {
    try {
      isLoading.value = true
      error.value = null

      const response = await api.post<ApiResponse<PurchaseResponse>>('/loyalty/purchase-credit', {
        package_key: packageKey
      })

      if (response.data.success && response.data.data) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Failed to purchase credit package')
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to purchase credit package'
      console.error('Error purchasing credit package:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Format cents to Euro string
   */
  function formatCents(cents: number): string {
    return '€' + (cents / 100).toFixed(2).replace('.', ',')
  }

  /**
   * Get tier color class (falls back to bronze for unknown/custom tiers)
   */
  function getTierColor(tierKey: string): string {
    const colors: Record<string, string> = {
      bronze: 'text-amber-700',
      silver: 'text-gray-500',
      gold: 'text-yellow-500',
      platinum: 'text-cyan-400'
    }
    return colors[tierKey] || colors.bronze
  }

  /**
   * Get tier gradient class (falls back to bronze for unknown/custom tiers)
   */
  function getTierGradient(tierKey: string): string {
    const gradients: Record<string, string> = {
      bronze: 'from-amber-700 to-amber-900',
      silver: 'from-gray-400 to-gray-600',
      gold: 'from-yellow-400 to-yellow-600',
      platinum: 'from-cyan-300 to-amber-500'
    }
    return gradients[tierKey] || gradients.bronze
  }

  return {
    // State
    tiers,
    creditPackages,
    myTierInfo,
    isLoading,
    error,

    // Computed
    currentTier,
    nextTier,
    progressPercentage,

    // Actions
    fetchTiers,
    fetchCreditPackages,
    fetchMyTier,
    previewPackagePurchase,
    purchaseCreditPackage,

    // Utilities
    formatCents,
    getTierColor,
    getTierGradient
  }
}
